/**
 * Teacher Assignment Form - Create/Edit Assignment
 * Allows teachers to create or edit assignments with PDF uploads
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { 
  ArrowLeft, 
  Save, 
  Upload,
  X,
  FileText,
  AlertCircle,
  Loader
} from 'lucide-react';

interface TeachingAssignment {
  id: string;
  classroom_id: string;
  classroom_name: string;
  subject_id: string;
  subject_name: string;
}

interface Attachment {
  id: string;
  file_name: string;
  size_bytes: number;
  created_at: number;
}

export function TeacherAssignmentForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [teachingAssignments, setTeachingAssignments] = useState<TeachingAssignment[]>([]);
  const [formData, setFormData] = useState({
    classroom_id: '',
    subject_id: '',
    title: '',
    description: '',
    due_at: '',
    status: 'draft' as 'draft' | 'published'
  });
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploadQueue, setUploadQueue] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTeachingAssignments();
    if (isEdit) {
      loadAssignment();
    }
  }, [id]);

  const loadTeachingAssignments = async () => {
    try {
      const response = await apiService.getMyTeaching();
      setTeachingAssignments(response.data || []);
    } catch (err) {
      console.error('Failed to load teaching assignments:', err);
    }
  };

  const loadAssignment = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const response = await apiService.getAssignment(id);
      const assignment = response.data;

      setFormData({
        classroom_id: assignment.classroom_id,
        subject_id: assignment.subject_id,
        title: assignment.title,
        description: assignment.description || '',
        due_at: assignment.due_at ? new Date(assignment.due_at).toISOString().slice(0, 16) : '',
        status: assignment.status
      });
      setAttachments(assignment.attachments || []);
    } catch (err) {
      console.error('Failed to load assignment:', err);
      setError(err instanceof Error ? err.message : 'Failed to load assignment');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    if (!formData.classroom_id || !formData.subject_id) {
      setError('Please select a class and subject');
      return;
    }

    try {
      setIsSaving(true);

      const data = {
        ...formData,
        due_at: formData.due_at ? new Date(formData.due_at).getTime() : undefined
      };

      let assignmentId = id;

      if (isEdit) {
        await apiService.updateAssignment(id!, data);
      } else {
        const response = await apiService.createAssignment(data);
        assignmentId = response.data.id;
      }

      // Upload queued files
      if (uploadQueue.length > 0 && assignmentId) {
        await uploadFiles(assignmentId);
      }

      navigate(`/teacher/assignments/${assignmentId}`);
    } catch (err) {
      console.error('Failed to save assignment:', err);
      setError(err instanceof Error ? err.message : 'Failed to save assignment');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const pdfFiles = files.filter(f => f.type === 'application/pdf');

    if (pdfFiles.length !== files.length) {
      alert('Only PDF files are allowed');
    }

    const oversized = pdfFiles.filter(f => f.size > 10 * 1024 * 1024);
    if (oversized.length > 0) {
      alert('Some files exceed 10MB limit and will be skipped');
    }

    const validFiles = pdfFiles.filter(f => f.size <= 10 * 1024 * 1024);
    setUploadQueue(prev => [...prev, ...validFiles]);
    e.target.value = '';
  };

  const removeFromQueue = (index: number) => {
    setUploadQueue(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async (assignmentId: string) => {
    setIsUploading(true);
    const errors: string[] = [];

    for (const file of uploadQueue) {
      try {
        await apiService.uploadAssignmentAttachment(assignmentId, file);
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
        errors.push(file.name);
      }
    }

    setUploadQueue([]);
    setIsUploading(false);

    if (errors.length > 0) {
      alert(`Failed to upload: ${errors.join(', ')}`);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!confirm('Delete this attachment?')) return;

    try {
      await apiService.deleteAssignmentAttachment(attachmentId);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
    } catch (err) {
      console.error('Failed to delete attachment:', err);
      alert('Failed to delete attachment');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (!user) return null;

  return (
    <Layout
      schoolName={'SMS'}
      principalName={user.loginId || 'Teacher'}
      onLogout={logout}
      role="teacher"
    >
      <div style={{ padding: '32px', maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <button
            onClick={() => navigate('/teacher/assignments')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              background: 'none',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              cursor: 'pointer',
              color: '#64748b',
              marginBottom: '16px'
            }}
          >
            <ArrowLeft size={16} />
            Back to Assignments
          </button>
          <h1 style={{ fontSize: '28px', fontWeight: 600, color: '#0f172a', marginBottom: '8px' }}>
            {isEdit ? 'Edit Assignment' : 'Create New Assignment'}
          </h1>
          <p style={{ fontSize: '14px', color: '#64748b' }}>
            {isEdit ? 'Update assignment details and attachments' : 'Fill in the details below to create an assignment'}
          </p>
        </div>

        {error && (
          <div style={{ marginBottom: '24px' }}>
            <Card>
              <div style={{ padding: '16px', background: '#fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
            </Card>
          </div>
        )}

        {isLoading ? (
          <Card>
            <div style={{ padding: '32px' }}>
              <Skeleton height="400px" />
            </div>
          </Card>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '24px' }}>
              <Card>
              <div style={{ padding: '32px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a', marginBottom: '24px' }}>
                  Assignment Details
                </h2>

                {/* Class and Subject Selection */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>
                      Class <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <select
                      value={formData.classroom_id}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, classroom_id: e.target.value, subject_id: '' }));
                      }}
                      disabled={isEdit}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: isEdit ? '#f9fafb' : 'white'
                      }}
                    >
                      <option value="">Select a class</option>
                      {Array.from(new Set(teachingAssignments.map(ta => ta.classroom_id))).map(classroomId => {
                        const ta = teachingAssignments.find(t => t.classroom_id === classroomId);
                        return (
                          <option key={classroomId} value={classroomId}>
                            {ta?.classroom_name}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>
                      Subject <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <select
                      value={formData.subject_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, subject_id: e.target.value }))}
                      disabled={!formData.classroom_id || isEdit}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: (!formData.classroom_id || isEdit) ? '#f9fafb' : 'white'
                      }}
                    >
                      <option value="">Select a subject</option>
                      {teachingAssignments
                        .filter(ta => ta.classroom_id === formData.classroom_id)
                        .map(ta => (
                          <option key={ta.id} value={ta.subject_id}>
                            {ta.subject_name}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                {/* Title */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>
                    Assignment Title <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    maxLength={200}
                    required
                    placeholder="e.g., Chapter 5 Homework"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                {/* Description */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    maxLength={2000}
                    rows={4}
                    placeholder="Add instructions or details about the assignment..."
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>

                {/* Due Date and Status */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>
                      Due Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.due_at}
                      onChange={(e) => setFormData(prev => ({ ...prev, due_at: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    >
                      <option value="draft">Draft (not visible to students)</option>
                      <option value="published">Published (visible to students)</option>
                      {isEdit && <option value="closed">Closed (no longer accepting)</option>}
                    </select>
                  </div>
                </div>
              </div>
            </Card>
            </div>

            {/* Attachments Section */}
            <Card>
              <div style={{ padding: '32px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a', marginBottom: '16px' }}>
                  Attachments (PDF only)
                </h2>

                {/* Upload Area */}
                <div style={{ marginBottom: '24px' }}>
                  <label
                    style={{
                      display: 'block',
                      padding: '32px',
                      border: '2px dashed #cbd5e1',
                      borderRadius: '8px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      background: '#f8fafc'
                    }}
                  >
                    <Upload size={48} style={{ color: '#94a3b8', margin: '0 auto 16px' }} />
                    <div style={{ fontSize: '14px', color: '#475569', marginBottom: '8px' }}>
                      <strong>Click to upload</strong> or drag and drop
                    </div>
                    <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                      PDF files only, max 10MB each
                    </div>
                    <input
                      type="file"
                      accept=".pdf,application/pdf"
                      multiple
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>

                {/* Upload Queue */}
                {uploadQueue.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
                      Queued for Upload ({uploadQueue.length})
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {uploadQueue.map((file, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px',
                            background: '#fef3c7',
                            border: '1px solid #fde68a',
                            borderRadius: '6px'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <FileText size={20} style={{ color: '#92400e' }} />
                            <div>
                              <div style={{ fontSize: '14px', color: '#78350f', fontWeight: 500 }}>
                                {file.name}
                              </div>
                              <div style={{ fontSize: '12px', color: '#92400e' }}>
                                {formatFileSize(file.size)}
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFromQueue(index)}
                            style={{
                              padding: '4px',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#92400e'
                            }}
                          >
                            <X size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Existing Attachments */}
                {attachments.length > 0 && (
                  <div>
                    <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
                      Current Attachments ({attachments.length})
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {attachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px',
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <FileText size={20} style={{ color: '#64748b' }} />
                            <div>
                              <div style={{ fontSize: '14px', color: '#0f172a', fontWeight: 500 }}>
                                {attachment.file_name}
                              </div>
                              <div style={{ fontSize: '12px', color: '#64748b' }}>
                                {formatFileSize(attachment.size_bytes)} • Uploaded {new Date(attachment.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteAttachment(attachment.id)}
                            style={{
                              padding: '6px 12px',
                              background: '#fee2e2',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              color: '#dc2626',
                              fontSize: '13px',
                              fontWeight: 500
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Action Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/teacher/assignments')}
                disabled={isSaving || isUploading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSaving || isUploading}
              >
                {isSaving || isUploading ? (
                  <>
                    <Loader size={16} style={{ marginRight: '8px' }} className="spin" />
                    {isUploading ? 'Uploading...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <Save size={16} style={{ marginRight: '8px' }} />
                    {isEdit ? 'Update Assignment' : 'Create Assignment'}
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </Layout>
  );
}
